import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import {
  BarChart3,
  TrendingUp,
  Users,
  DollarSign,
  Plus,
  Play,
  Edit,
  Trash2,
  Loader2,
  ChevronRight,
  Eye,
  Clock,
  Trophy,
  Building2,
  Target,
  ClipboardList,
  AlertTriangle,
  Download,
  FileDown,
  Megaphone,
  Package,
  LineChart,
  PresentationIcon as Presentation,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useUser } from '@/contexts/UserContext'
import {
  useCustomReports,
  useExecuteCustomReport,
  useDeleteCustomReport,
  useExportReportPdf,
} from '@/hooks/use-api'
import { formatDistanceToNow } from 'date-fns'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ReportPreview } from '@/components/reports'
import { exportToCSV } from '@/utils/csvExport'
import { CATEGORICAL, GRACKLE_COLORS } from '@/lib/theme'
import { explainError } from '@/lib/explain-error'

export default function Reports() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { isAdmin } = useUser()
  const { data: savedReportsData, isLoading: reportsLoading } = useCustomReports()
  const executeReport = useExecuteCustomReport()
  const viewExecuteReport = useExecuteCustomReport()
  const deleteReport = useDeleteCustomReport()
  const exportPdf = useExportReportPdf()

  const [executingReportId, setExecutingReportId] = useState<string | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [reportToDelete, setReportToDelete] = useState<{ id: string; name: string } | null>(null)
  // Saved-report "View" modal: holds the report being viewed and its run results.
  const [viewReport, setViewReport] = useState<any | null>(null)
  const [viewResults, setViewResults] = useState<{ data: Record<string, any>[] } | null>(null)

  // Report catalog. `group` drives the grouped sections below; GROUP_ORDER sets
  // section order. `adminOnly` items are hidden from non-admins.
  const reportTypes = [
    {
      id: 'exec-sales-dashboard',
      title: 'Exec Sales Dashboard',
      description: 'Executive sales metrics, pipeline health, and performance indicators',
      icon: Presentation,
      categoryFamily: CATEGORICAL.raspberry,
      route: '/reports/exec-sales-dashboard',
      group: 'Executive',
      adminOnly: true,
      badge: 'Admin',
    },
    {
      id: 'sales-forecast',
      title: 'Sales Forecast',
      description: 'Pipeline forecasting and revenue projections',
      icon: TrendingUp,
      categoryFamily: CATEGORICAL.navy,
      route: '/analytics/sales-forecast',
      group: 'Pipeline & Forecast',
    },
    {
      id: 'pipeline',
      title: 'Pipeline Report',
      description: 'Opportunity pipeline and conversion metrics',
      icon: BarChart3,
      categoryFamily: CATEGORICAL.clay,
      route: '/reports/pipeline',
      group: 'Pipeline & Forecast',
    },
    {
      id: 'big-opportunities',
      title: 'Big Opportunities',
      description: 'Track high-value deals in the pipeline',
      icon: Target,
      categoryFamily: CATEGORICAL.raspberry,
      route: '/reports/big-opportunities',
      group: 'Pipeline & Forecast',
    },
    {
      id: 'stuck-opportunities',
      title: 'Stuck Opportunities',
      description: 'Open deals stalled past their stage, ranked by value at risk',
      icon: AlertTriangle,
      categoryFamily: CATEGORICAL.raspberry,
      route: '/reports/stuck-opportunities',
      group: 'Pipeline & Forecast',
    },
    {
      id: 'revenue',
      title: 'Revenue Analytics',
      description: 'Revenue performance and trends analysis',
      icon: DollarSign,
      categoryFamily: CATEGORICAL.evergreen,
      route: '/analytics',
      group: 'Revenue & Performance',
    },
    {
      id: 'closed-won-monthly',
      title: 'Month over Month - Closed Won',
      description: 'Closed won opportunities by month with owner breakdown',
      icon: TrendingUp,
      categoryFamily: CATEGORICAL.powder,
      route: '/reports/closed-won-monthly',
      group: 'Revenue & Performance',
    },
    {
      id: 'win-loss-analysis',
      title: 'Win/Loss Analysis',
      description: 'Analyze win rates and loss patterns',
      icon: Trophy,
      categoryFamily: CATEGORICAL.pollen,
      route: '/reports/win-loss-analysis',
      group: 'Revenue & Performance',
    },
    {
      id: 'days-to-close',
      title: 'Days to Close Analysis',
      description: 'Analyze sales cycle length and velocity metrics',
      icon: Clock,
      categoryFamily: CATEGORICAL.teal,
      route: '/reports/days-to-close',
      group: 'Revenue & Performance',
    },
    {
      id: 'product-revenue',
      title: 'Product by Revenue',
      description: 'Realized (Closed-Won) revenue broken down by product, grouped by family',
      icon: Package,
      categoryFamily: CATEGORICAL.evergreen,
      route: '/reports/product-revenue',
      group: 'Revenue & Performance',
    },
    {
      id: 'revenue-trend',
      title: 'Revenue Trend (TTM)',
      description:
        'Trailing-twelve-month software or services revenue, and pace against last fiscal year',
      icon: LineChart,
      categoryFamily: CATEGORICAL.prussian,
      route: '/reports/revenue-trend',
      group: 'Revenue & Performance',
    },
    {
      id: 'top-accounts',
      title: 'Top Accounts',
      description: 'Analyze highest-value customer accounts',
      icon: Building2,
      categoryFamily: CATEGORICAL.plum,
      route: '/reports/top-accounts',
      group: 'Revenue & Performance',
    },
    {
      id: 'lead-sources',
      title: 'Lead Source Analysis',
      description: 'Detailed lead source conversion rates and performance',
      icon: Users,
      categoryFamily: CATEGORICAL.prussian,
      route: '/analytics/lead-sources',
      group: 'Marketing & Leads',
    },
    {
      id: 'marketing',
      title: 'Marketing Reports',
      description:
        'Marketing-generated pipeline and revenue by lead source, single month or by-month trend',
      icon: Megaphone,
      categoryFamily: CATEGORICAL.prussian,
      route: '/reports/marketing',
      group: 'Marketing & Leads',
    },
    {
      id: 'marketing-funnel',
      title: 'Marketing Funnel',
      description:
        'Contact lifecycle funnel (Contact → MQL), revenue funnel (MQL → closed), and lead source by stage',
      icon: Megaphone,
      categoryFamily: CATEGORICAL.navy,
      route: '/reports/marketing-funnel',
      group: 'Marketing & Leads',
    },
    {
      id: 'activities-created',
      title: 'Activities Created - This Month',
      description: 'Activities created and completed this month by team member',
      icon: ClipboardList,
      categoryFamily: CATEGORICAL.steel,
      route: '/reports/activities-created',
      group: 'Activity & Teams',
    },
    {
      id: 'mtd-teams',
      title: 'MTD - Teams',
      description: 'Month-to-date opportunities grouped by owner with weighted amounts',
      icon: Users,
      categoryFamily: CATEGORICAL.slate,
      route: '/reports/mtd-teams',
      group: 'Activity & Teams',
    },
  ]
  const GROUP_ORDER = [
    'Executive',
    'Pipeline & Forecast',
    'Revenue & Performance',
    'Activity & Teams',
    'Marketing & Leads',
  ]

  const savedReports =
    (savedReportsData as { data?: unknown[] } | undefined)?.data || savedReportsData || []

  const handleRunReport = async (reportId: string) => {
    try {
      setExecutingReportId(reportId)
      const result = await executeReport.mutateAsync(reportId)

      toast({
        title: 'Report Executed',
        description: `Generated ${result.rowCount ?? result.results?.length ?? 0} results`,
      })

      // For now, we'll just show the success message
      // In a production app, you might want to navigate to a results page or show a modal
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to execute report')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setExecutingReportId(null)
    }
  }

  // Open the View modal and run the report so its chart/table can be shown read-only.
  const handleViewReport = async (report: any) => {
    setViewReport(report)
    setViewResults(null)
    try {
      const result = await viewExecuteReport.mutateAsync(report.id)
      // ReportPreview expects results under a `data` key.
      setViewResults({ data: result.results ?? [] })
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to run report'),
        variant: 'destructive',
      })
    }
  }

  const handleViewExportCsv = () => {
    if (!viewReport || !viewResults?.data || viewResults.data.length === 0) {
      toast({
        title: 'No Data',
        description: 'There are no results to export.',
        variant: 'destructive',
      })
      return
    }
    try {
      exportToCSV({ data: viewResults.data, filename: `${viewReport.name || 'report'}.csv` })
      toast({ title: 'Export Successful', description: 'Report has been exported to CSV.' })
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to export report'),
        variant: 'destructive',
      })
    }
  }

  const handleViewExportPdf = async () => {
    if (!viewReport) return
    try {
      const pdfBlob = await exportPdf.mutateAsync({
        dataSource: viewReport.data_source,
        config: viewReport.config || {},
        reportName: viewReport.name || 'Custom Report',
      })
      const url = window.URL.createObjectURL(pdfBlob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${viewReport.name || 'report'}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast({ title: 'PDF Exported', description: 'Report has been exported to PDF.' })
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to export PDF'),
        variant: 'destructive',
      })
    }
  }

  const handleDeleteReport = (reportId: string, reportName: string) => {
    setReportToDelete({ id: reportId, name: reportName })
    setDeleteDialogOpen(true)
  }

  const confirmDeleteReport = async () => {
    if (!reportToDelete) return

    try {
      await deleteReport.mutateAsync(reportToDelete.id)

      toast({
        title: 'Report Deleted',
        description: `"${reportToDelete.name}" has been deleted successfully.`,
      })
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to delete report')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setDeleteDialogOpen(false)
      setReportToDelete(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Reports</h1>
          <p className="text-muted-foreground">
            Generate and download various reports for your CRM data
          </p>
        </div>
        <Button
          onClick={() => navigate('/reports/builder')}
          aria-label="Open custom report builder"
        >
          <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
          Custom Report Builder
        </Button>
      </div>

      <div className="space-y-10">
        {GROUP_ORDER.map((group) => {
          const items = reportTypes.filter(
            (r) => r.group === group && (!(r as { adminOnly?: boolean }).adminOnly || isAdmin)
          )
          if (items.length === 0) return null
          return (
            <section key={group} className="space-y-4" aria-label={`${group} reports`}>
              <div className="flex items-center gap-2.5 border-b-2 border-border pb-2">
                <span
                  className="h-6 w-1.5 rounded-sm"
                  style={{ backgroundColor: CATEGORICAL.raspberry.solid }}
                  aria-hidden="true"
                />
                <h2
                  className="text-xl font-bold tracking-tight"
                  style={{ color: CATEGORICAL.raspberry.solid }}
                >
                  {group}
                </h2>
                <span className="ml-auto text-xs font-medium text-muted-foreground">
                  {items.length} {items.length === 1 ? 'report' : 'reports'}
                </span>
              </div>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {items.map((report) => {
                  const Icon = report.icon
                  const { tint, solid } = report.categoryFamily
                  // Light-only families (Pollen, Powder) have solid === tint, so the
                  // family solid would be an invisible glyph on its own tile. Fall
                  // back to Prussian, which is >= 6:1 on every tint.
                  const glyphColor = solid === tint ? GRACKLE_COLORS.prussianBlue : solid
                  const badge = (report as { badge?: string }).badge ?? 'Pre-built'
                  return (
                    <Card
                      key={report.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate(report.route)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          navigate(report.route)
                        }
                      }}
                      aria-label={`View ${report.title}`}
                      className="flex flex-col cursor-pointer transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <CardHeader className="flex-1 bg-transparent border-b-0">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div
                              className="flex h-8 w-8 items-center justify-center rounded"
                              style={{ backgroundColor: tint }}
                            >
                              <Icon
                                className="h-5 w-5"
                                style={{ color: glyphColor }}
                                aria-hidden="true"
                              />
                            </div>
                            <CardTitle>{report.title}</CardTitle>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">{badge}</Badge>
                            <ChevronRight
                              className="h-4 w-4 text-muted-foreground"
                              aria-hidden="true"
                            />
                          </div>
                        </div>
                        <CardDescription>{report.description}</CardDescription>
                      </CardHeader>
                    </Card>
                  )
                })}
              </div>
            </section>
          )
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Saved Custom Reports</CardTitle>
          <CardDescription>Your saved custom report configurations</CardDescription>
        </CardHeader>
        <CardContent>
          {reportsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2
                className="h-6 w-6 animate-spin text-muted-foreground"
                aria-label="Loading saved reports"
              />
            </div>
          ) : savedReports.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-8">
              No saved reports yet. Create your first custom report to see it here.
            </div>
          ) : (
            <div className="space-y-3">
              {savedReports.map((report: any) => (
                <div
                  key={report.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium">{report.name}</h3>
                      <Badge variant="outline" className="text-xs">
                        {report.data_source}
                      </Badge>
                      {report.config?.chartType && report.config.chartType !== 'table' && (
                        <Badge variant="secondary" className="text-xs">
                          {report.config.chartType}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {report.config?.fields?.length || 0} fields
                      {report.config?.filters?.length > 0 &&
                        ` • ${report.config.filters.length} filters`}
                      {report.last_run_at && (
                        <>
                          {' '}
                          • Last run{' '}
                          {formatDistanceToNow(new Date(report.last_run_at), { addSuffix: true })}
                        </>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewReport(report)}
                      aria-label={`View report ${report.name}`}
                    >
                      <Eye className="mr-2 h-4 w-4" aria-hidden="true" />
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRunReport(report.id)}
                      disabled={executingReportId === report.id}
                      aria-label={`Run report ${report.name}`}
                    >
                      {executingReportId === report.id ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                      ) : (
                        <Play className="mr-2 h-4 w-4" aria-hidden="true" />
                      )}
                      Run
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/reports/builder?id=${report.id}`)}
                      aria-label={`Edit report ${report.name}`}
                    >
                      <Edit className="mr-2 h-4 w-4" aria-hidden="true" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteReport(report.id, report.name)}
                      disabled={deleteReport.isPending}
                      aria-label={`Delete report ${report.name}`}
                    >
                      <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Report</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{reportToDelete?.name}"? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteReport}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Report Modal */}
      <Dialog
        open={!!viewReport}
        onOpenChange={(open) => {
          if (!open) {
            setViewReport(null)
            setViewResults(null)
          }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewReport?.name || 'Report'}</DialogTitle>
            <DialogDescription>
              {viewReport?.data_source} report
              {viewResults ? ` • ${viewResults.data.length} results` : ''}
            </DialogDescription>
          </DialogHeader>
          <ReportPreview
            title="Results"
            chartType={viewReport?.config?.chartType || 'table'}
            reportResults={viewResults}
            comparisonResults={null}
            isExecuting={viewExecuteReport.isPending}
            isLivePreviewEnabled={false}
            onLivePreviewChange={() => {}}
            showLiveToggle={false}
          />
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={handleViewExportCsv}
              disabled={!viewResults?.data || viewResults.data.length === 0}
              aria-label="Export report to CSV"
            >
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleViewExportPdf}
              disabled={!viewResults?.data || viewResults.data.length === 0 || exportPdf.isPending}
              aria-label="Export report to PDF"
            >
              {exportPdf.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <FileDown className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
